import styled from '@emotion/styled'

import { fontSettings } from '@core/components/Button/types'
import { Image } from '@core/components/Image'
import { THUMBNAIL_HEIGHT, THUMBNAIL_WIDTH } from '@core/styles/cssVariables'
import UnstyledButton from '@embed/components/common/UnstyledButton'

const PLAYER_CARD_HEIGHT_WITH_SINGLE_PRODUCT = '72px'

export const PlayerDeckHeader = styled.div`
  position: absolute;
  z-index: 2;
  padding: 8px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  top: 0;
  right: 0;
`

export const PlayerDeckHeaderCaption = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 6px;
  padding-inline-start: 4px;
  gap: 4px;
  text-shadow: 0 0 7px rgba(0, 0, 0, 0.4);
`

export const PlayerDeckHeaderControls = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 4px;
  height: 32px;
`

export const PlayerDeckHeaderControlButton = styled(UnstyledButton)`
  cursor: pointer;
  width: 32px;
  height: 32px;
`

export const StyledThumbnailContainerV2 = styled.div<{
  height: number
  responsive?: boolean
  columnWidth?: string
  maxWidth?: number
  minWidth?: number
  isGridMode?: boolean
}>`
  position: relative;
  border-radius: var(--border-radius, 11px);
  border: 1px solid var(--player-deck-border-color, #e1e4e8);

  ${({ responsive, columnWidth, minWidth, maxWidth, isGridMode }) =>
    isGridMode
      ? `width: 100%;`
      : responsive
        ? `
        flex: 0 0 ${columnWidth};
        ${minWidth ? `min-width: ${minWidth}px;` : ''}
        ${maxWidth ? `max-width: ${maxWidth}px;` : ''}
        `
        : `height: var(
    ${THUMBNAIL_HEIGHT},
    calc(
      ${({ height }) => height}px - ${PLAYER_CARD_HEIGHT_WITH_SINGLE_PRODUCT}
    )
  );`}

  background-color: var(--player-deck-list-background, #fff);
  box-sizing: content-box;
  padding-bottom: ${PLAYER_CARD_HEIGHT_WITH_SINGLE_PRODUCT};

  // Keyboard focus ring around the whole card. Drawn as an overlay
  // pseudo-element rather than 'outline' for the same reason ThumbnailWrapper's
  // Inner does it: a positive outline-offset lands in the scrolling track's
  // clipped region (overflow: scroll hidden), so the ring barely shows.
  // 'inset: 0' resolves against the padding box, so the ring frames the card
  // including the product strip — the same region the outline framed.
  // '.focus-visible' covers the Safari/iOS focus-visible polyfill.
  //
  // Deliberately card-only (no ':has(:focus-visible)'): descendants carry their
  // own rings — Inner's '::after' for the play button — and lifting their state
  // up here would draw a second ring around the whole card at the same time.
  &:focus-visible::after,
  &.focus-visible::after {
    content: '';
    position: absolute;
    inset: 0;
    box-sizing: border-box;
    border: 2px solid var(--player-deck-focus-outline-color, #1a73e8);
    border-radius: var(--border-radius, 11px);
    pointer-events: none;
    z-index: 3;
  }

  &:focus {
    outline: none;
  }
`

export const ThumbnailContainer = styled.div<{
  height: number
  width: number
  responsive?: boolean
  isGridMode?: boolean
}>`
  position: relative;
  ${({ responsive, height, width, isGridMode }) =>
    isGridMode
      ? `width: 100%;`
      : responsive
        ? ``
        : `
      height: var(${THUMBNAIL_HEIGHT}, ${height}px);
      width: var(${THUMBNAIL_WIDTH}, ${width}px);
    `}
`

export const ProductListWrapper = styled.div<{
  showAllProducts: boolean
}>`
  display: flex;
  flex-direction: column;
  height: ${({ showAllProducts }) =>
    showAllProducts
      ? 'calc(100% - 80px)'
      : PLAYER_CARD_HEIGHT_WITH_SINGLE_PRODUCT};
  width: 100%;
  position: absolute;
  bottom: 0;
  border-radius: 0 0 var(--border-radius, 11px) var(--border-radius, 11px);
  ${({ showAllProducts }) =>
    !showAllProducts &&
    `
    border-top: 1px solid rgba(0, 0, 0, 0.1);
  `}
`

export const ProductCardItemWrapper = styled.div`
  display: flex;
  align-items: center;
`

export const CTAWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 72px;
  width: 100%;
  position: absolute;
  bottom: 0;
`

export const CTALink = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 0 0 var(--border-radius, 11px) var(--border-radius, 11px);
  padding: 8px 16px;
  width: 100%;
  height: 100%;
  text-decoration: none !important;
  color: var(--player-deck-cta-button-color);
  text-overflow: ellipsis;
  overflow: hidden;
  cursor: pointer;
  font-family: var(--font-family);
  font-size: var(--button-font-size, ${fontSettings.default}px);
  font-weight: var(--button-font-weight, 600);
  background-color: var(--player-deck-cta-background-color);
`
export const LivestreamCTA = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 0 0 var(--border-radius, 11px) var(--border-radius, 11px);
  padding: 8px 16px;
  width: 100%;
  height: 100%;
  color: var(--player-deck-cta-button-color);
  text-overflow: ellipsis;
  overflow: hidden;
  cursor: pointer;
  font-family: var(--font-family);
  font-size: var(--button-font-size, ${fontSettings.default}px);
  font-weight: var(--button-font-weight, 600);
  background-color: var(--player-deck-cta-background-color);
  border: none;
  :dir(rtl) {
    flex-direction: row-reverse;
  }
`

export const ToggleButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 8px 17px;
  height: 48px;
`

export const ProductList = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: 100%;
  width: 100%;
  color: black;
  gap: 2px;
  border-radius: var(--border-radius, 11px);
  overflow: auto;
  background-color: var(--player-deck-list-background, #fff);
  backdrop-filter: blur(4px);
  &::-webkit-scrollbar {
    display: none;
  }
`

export const ProductCardGrid = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  height: 72px;
  padding: 4px;
  /* Let the card shrink below its nowrap price row so a sibling (the
     "Show All Products" chevron) isn't pushed out and clipped. */
  min-width: 0;

  background: var(--player-deck-item-background);
  backdrop-filter: blur(15px);

  &:hover,
  &:focus-within {
    background: var(--player-deck-item-background-hover);
  }
`
export const ProductLink = styled.a<{ withAddToCart: boolean }>`
  text-decoration: none !important;
  color: var(--player-deck-product-card-font-color) !important;
  font-size: var(--player-deck-product-card-font-size, 12px);
  width: ${({ withAddToCart }) =>
    withAddToCart ? 'calc(100% - 60px)' : '100%'};
  min-width: 0;
`
export const ProductCard = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  overflow: hidden;
  align-items: stretch;
`

export const ProductImage = styled(Image)`
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  object-fit: var(--product-card-image-object-fit);
  object-position: 50% 50%;
  border-radius: 6px;
`

export const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 6px;
  /* Allow the column to shrink below its content width so the nowrap
     subtitle can be clipped with an ellipsis instead of overflowing. */
  min-width: 0;
`

export const ProductTitle = styled.div<{ singleLine?: boolean }>`
  font-weight: var(--player-deck-product-card-font-weight, 500);
  max-width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: ${({ singleLine }) => (singleLine ? 1 : 2)};
  -webkit-box-orient: vertical;
`

export const ProductSubtitle = styled.div`
  font-weight: 500;
  font-size: 12px;
  color: var(--player-deck-product-card-subtitle-font-color, #6b6b6b);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  /* Same single-line truncation mechanism as ProductTitle, which renders
     the ellipsis reliably inside this flex column. */
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
`

export const CustomProductPriceStyle = styled.span`
  font-family: var(--product-price-font-family, inherit);
  color: var(--product-price-font-color, inherit);
`

export const PriceWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 4px;
`

export const Price = styled.div`
  font-weight: var(--player-deck-product-card-font-weight, 600);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  /* Inside PriceWrapper, keep the discounted price whole; OriginalPrice
     absorbs the truncation instead. */
  flex-shrink: 0;
`

export const OriginalPrice = styled.span`
  font-weight: var(--player-deck-product-card-font-weight, 500);
  // by default card font size is 12px and og price is 10px
  // 10 / 12 = .833
  font-size: 0.833em;
  opacity: 0.65;
  text-decoration: line-through;
  /* When space runs out, truncate the original price rather than the
     discounted one the shopper actually pays. */
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const DiscountPercent = styled.span`
  font-weight: var(--player-deck-product-card-font-weight, 500);
  color: var(
    --player-deck-product-card-discount-price-font-color,
    #b60000
  ) !important;
  align-self: flex-end;
  flex-shrink: 0;
`

export const IconButton = styled.button<{
  boxShadow?: boolean
  hoverBackground?: boolean
}>`
  background: var(--player-deck-product-drawer-trigger-background, #fff);
  border-radius: 50%;
  box-sizing: border-box;
  color: var(--player-deck-product-drawer-trigger-foreground, #000) !important;
  width: 32px;
  height: 32px;
  border: 0px;
  cursor: pointer;
  ${({ boxShadow }) => boxShadow && `box-shadow: 0px 1px 4px 0px #00000033;`}
  ${({ hoverBackground }) =>
    hoverBackground &&
    `
    &:hover {
      background: rgba(0, 0, 0, 0.05);
    }
  `}
`
export const SoldOutLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
`

export const ProductSecondaryCTAButtonWrapper = styled.div`
  position: absolute;
  bottom: 12px;
  left: 0;
  font-family: var(--font-family);
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
`
